import {
  createContext,
  FC,
  PropsWithChildren,
  useEffect,
  useState,
} from 'react';
import useAuth from 'ui/hooks/useAuth';
import useClientQuery from 'ui/hooks/useClientQuery';
import useRedirectToDashboard from 'ui/hooks/useRedirectToDashboard';

export interface LoginFlowContextValue {
  isLoginFlowDetermined: boolean;
}

export const LoginFlowContext = createContext<LoginFlowContextValue | null>(
  null,
);

const LoginFlowProvider: FC<PropsWithChildren> = (props) => {
  const { loggingInStatus, triggerUnauthorized } = useAuth();
  const [isLoginFlowDetermined, setIsLoginFlowDetermined] = useState(true);
  const { status: clientStatus, data: clientData } = useClientQuery();
  const { redirectToDashboard } = useRedirectToDashboard();

  useEffect(() => {
    if (loggingInStatus === 'loading') setIsLoginFlowDetermined(false);
  }, [loggingInStatus]);

  useEffect(() => {
    if (isLoginFlowDetermined) return;

    if (loggingInStatus === 'error') {
      setIsLoginFlowDetermined(true);
      return;
    }

    if (loggingInStatus !== 'success') return;

    if (clientStatus !== 'success') return;

    if (!clientData) return;

    if (clientData.status === 'DELETED') {
      triggerUnauthorized();
    } else if (
      clientData.status === 'IDENTIFIED' ||
      clientData.status === 'ACTIVE' ||
      clientData.hasOpenLoan
    ) {
      redirectToDashboard();
    } else {
      setIsLoginFlowDetermined(true);
    }
  }, [
    isLoginFlowDetermined,
    loggingInStatus,
    clientData?.status,
    clientData?.hasOpenLoan,
    clientData?.hasOpenLoanApplication,
    clientStatus,
  ]);
  const loginFlowContextValue: LoginFlowContextValue = {
    isLoginFlowDetermined,
  };

  return (
    <LoginFlowContext.Provider value={loginFlowContextValue}>
      {props.children}
    </LoginFlowContext.Provider>
  );
};

export default LoginFlowProvider;
